import {
    getDoc,
    setDoc,
    deleteDoc,
    collection,
    getDocs,
} from 'firebase/firestore'
import { docRef } from '~common/utils/firestore-helpers'
import type { AutoFetchConfig } from '~common/storage/auto-fetch-config'
import { EXCLUDED_CONFIG_IDS } from '../constants'
import { StorageModuleDeps } from '~common/storage/types'

export class AutoFetchConfigStorageModule {
    constructor(private readonly deps: StorageModuleDeps) {}

    private get userId(): string {
        return this.deps.ensureAuthenticated()
    }

    async createAutoFetchConfig(
        domain: string,
        config: Omit<AutoFetchConfig, 'createdAt' | 'updatedAt'>,
    ): Promise<void> {
        const now = Date.now()
        const data: AutoFetchConfig = {
            ...config,
            createdAt: now,
            updatedAt: now,
        }

        const configRef = docRef<AutoFetchConfig>(
            this.deps.db,
            'users',
            this.userId,
            'autoFetchConfig',
            domain,
        )
        await setDoc(configRef, data)
    }

    async getAutoFetchConfig(domain: string): Promise<AutoFetchConfig | null> {
        const configRef = docRef<AutoFetchConfig>(
            this.deps.db,
            'users',
            this.userId,
            'autoFetchConfig',
            domain,
        )
        const configDoc = await getDoc(configRef)
        if (configDoc.exists()) {
            const data = configDoc.data()
            return {
                ...data,
                id: configDoc.id,
            } as AutoFetchConfig
        }
        return null
    }

    async updateAutoFetchConfig(
        domain: string,
        partial: Partial<Omit<AutoFetchConfig, 'createdAt'>>,
    ): Promise<void> {
        const configRef = docRef<AutoFetchConfig>(
            this.deps.db,
            'users',
            this.userId,
            'autoFetchConfig',
            domain,
        )
        const dataToUpdate = {
            ...partial,
            updatedAt: Date.now(),
        }
        await setDoc(configRef, dataToUpdate, { merge: true })
    }

    async deleteAutoFetchConfig(domain: string): Promise<void> {
        const configRef = docRef<AutoFetchConfig>(
            this.deps.db,
            'users',
            this.userId,
            'autoFetchConfig',
            domain,
        )
        await deleteDoc(configRef)
    }

    async getAutoFetchConfigs(): Promise<AutoFetchConfig[]> {
        const configsRef = collection(
            this.deps.db,
            'users',
            this.userId,
            'autoFetchConfig',
        )
        const snapshot = await getDocs(configsRef)
        return snapshot.docs
            .map((doc) => {
                const data = doc.data() as Omit<AutoFetchConfig, 'id'>
                return {
                    id: doc.id,
                    ...data,
                }
            })
            .filter((config) => !EXCLUDED_CONFIG_IDS.includes(config.id))
    }
}
